import * as React from "react";
import { chain, round } from "mathjs";
import { Box, Checkbox, FormControlLabel, FormGroup, TextField, Typography } from "@mui/material";

import { t, tl } from "../../../components/translate";
import NrsFormat from "../../../components/FormattingInput/NrsInput";
import { paymentOptionsEnum } from "./BillSummary";
import { BillDocumentType } from "../../../interfaces/BillInterfaces";
import useIsSsfBilling from "../../../hooks/useIsSsfBilling";

// @ts-expect-error - css
import styles from "./BillEditor.module.css";

/**
 * @todo refactor payment info for invoice bill and credit note (into two separate components) when there is time
 * @todo complexities are coming up ahead because of handling advance, too many if else cases are being handled
 */

interface PaymentInfo {
  draft: BillDocumentType;
  updateDraft: (v) => void;
  isCreditNote: boolean;
}

const PaymentInfo = ({ draft, updateDraft, isCreditNote }: PaymentInfo): React.JSX.Element => {
  const { isSsfBilling } = useIsSsfBilling();

  const paymentInfo = draft.paymentInfo
    ? { ...draft.paymentInfo }
    : {
        paid: true,
        paidAmount: draft.summary.totalAmount,
        paymentDistribution: {
          additionalPayment:
            (draft.client.balance ?? 0) < draft.summary.totalAmount
              ? chain(draft.summary.totalAmount)
                  .subtract(Number(draft.client.balance ?? 0))
                  .done()
              : 0,
          paymentFromBalance: draft.client.balance
        }
      };

  let dueAmt = (
    isCreditNote
      ? chain(draft?.refundInfo?.maximumRefundable || 0)
          .subtract(paymentInfo.paidAmount || 0)
          .done()
      : chain(draft?.summary?.totalAmount)
          .subtract(paymentInfo?.paidAmount || 0)
          .done() || 0
  ).toFixed(2);

  if (isSsfBilling) {
    if (
      paymentInfo.paymentDistribution?.additionalPayment ===
      draft.summary?.totalAmount - (paymentInfo?.paymentDistribution?.paymentFromSsf || 0)
    ) {
      dueAmt = "0.00";
    } else {
      dueAmt = chain(draft.summary?.totalAmount || 0)
        .subtract(paymentInfo?.paymentDistribution?.paymentFromSsf || 0)
        .done()
        .toFixed(2);
    }
  }

  return (
    <FormGroup>
      <FormControlLabel
        control={
          <Checkbox
            checked={paymentInfo.paid}
            disabled={isCreditNote || draft?.isProformaBill}
            data-testmation="billingPaidAll"
            onChange={(_, checked) => {
              if (isCreditNote) return;

              // TODO: handle paymentBreakdown in ssf billing
              if (isSsfBilling) {
                let paymentMethod = paymentOptionsEnum.cash;
                const paid = !!checked;
                if (!paid) {
                  paymentMethod = paymentOptionsEnum.noPayment;
                }
                updateDraft({
                  ...draft,
                  paymentInfo: { ...paymentInfo, paid, paymentMethod }
                });
              }

              if (!isSsfBilling) {
                // NOTE: the whole logic here is also usd in BillItem component
                // in one of the useEffect there
                // please update there as well if any changes are made here

                let paymentBreakdown = [...(draft?.paymentInfo?.paymentBreakdown || [])];

                const { totalAmount } = draft.summary;

                if (checked) {
                  if (paymentBreakdown.length === 1) {
                    if (paymentBreakdown[0].method === paymentOptionsEnum.balance) {
                      // leave the balance be and add new method
                      const remaining = Math.max(0, totalAmount - paymentBreakdown[0].amount);
                      paymentBreakdown = [
                        ...paymentBreakdown,
                        { method: paymentOptionsEnum.cash, amount: remaining }
                      ];
                    } else {
                      paymentBreakdown = [
                        {
                          method: paymentBreakdown[0].method || paymentOptionsEnum.cash,
                          amount: totalAmount
                        }
                      ];
                    }
                  } else if (paymentBreakdown.length > 1) {
                    const balanceEntry = paymentBreakdown.find(
                      (p) => p.method === paymentOptionsEnum.balance
                    );

                    if (balanceEntry) {
                      const balanceAmount = balanceEntry.amount;

                      const nonBalance = paymentBreakdown.filter(
                        (p) => p.method !== paymentOptionsEnum.balance
                      );

                      const nonBalanceSum = nonBalance.reduce((acc, cur) => acc + cur.amount, 0);
                      const needed = Math.max(0, totalAmount - balanceAmount - nonBalanceSum);

                      if (needed > 0) {
                        if (nonBalance.length === 0) {
                          paymentBreakdown = [
                            ...paymentBreakdown,
                            { method: paymentOptionsEnum.cash, amount: needed }
                          ];
                        } else {
                          paymentBreakdown = paymentBreakdown.map((p) => ({ ...p }));

                          // eslint-disable-next-line no-plusplus
                          for (let i = paymentBreakdown.length - 1; i >= 0; i--) {
                            if (paymentBreakdown[i].method !== paymentOptionsEnum.balance) {
                              paymentBreakdown[i].amount += needed;
                              break;
                            }
                          }
                        }
                      }
                    } else {
                      const nonBalanceSum = paymentBreakdown
                        .filter((p) => p.method !== paymentOptionsEnum.balance)
                        .reduce((acc, curr) => curr.amount + acc, 0);

                      const needed = Math.max(0, totalAmount - nonBalanceSum);

                      // eslint-disable-next-line no-plusplus
                      for (let i = paymentBreakdown.length - 1; i >= 0; i--) {
                        if (paymentBreakdown[i].method !== paymentOptionsEnum.balance) {
                          paymentBreakdown[i].amount += needed;
                          break;
                        }
                      }
                    }
                  }
                } else {
                  const balanceEntry = paymentBreakdown.find(
                    (p) => p.method === paymentOptionsEnum.balance
                  );

                  if (balanceEntry) {
                    paymentBreakdown = [{ ...balanceEntry }];
                  } else {
                    paymentBreakdown = [
                      {
                        method: paymentOptionsEnum.cash,
                        amount: 0
                      }
                    ];
                  }
                }

                const paidAmount = paymentBreakdown.reduce((sum, p) => sum + p.amount, 0);
                const paymentFromBalance =
                  paymentBreakdown.find((p) => p.method === paymentOptionsEnum.balance)?.amount ||
                  0;
                const additionalPayment = paymentBreakdown
                  .filter((p) => p.method !== paymentOptionsEnum.balance)
                  .reduce((acc, curr) => curr.amount + acc, 0);

                updateDraft({
                  ...draft,
                  paymentInfo: {
                    paidAmount,
                    paymentBreakdown,
                    paid: paidAmount >= draft.summary.totalAmount,
                    paymentDistribution: {
                      paymentFromSsf: 0,
                      additionalPayment,
                      paymentFromBalance
                    }
                  }
                });
              }
            }}
          />
        }
        label={isCreditNote ? t("billing.refundedAll") : t("billing.paidAll")}
      />

      {Boolean(!paymentInfo.paid || paymentInfo.paidAmount) && (
        <>
          {isSsfBilling && (
            <Box component="div" marginBottom="4px" display="flex">
              <Box
                component="span"
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                width="100%"
                fontWeight={500}
              >
                SSF Balance
              </Box>
              <Box component="span" marginLeft="16px" textAlign="left">
                <TextField
                  disabled
                  data-testmation="billingPaidFromBalance"
                  value={(paymentInfo.paymentDistribution?.paymentFromSsf || 0).toFixed(2)}
                  margin="dense"
                  variant="outlined"
                  slotProps={{
                    input: {
                      inputComponent: NrsFormat as any,
                      startAdornment: tl("rs"),
                      classes: { input: styles.nrsInput }
                    }
                  }}
                />
              </Box>
            </Box>
          )}

          <Box component="div" display="flex">
            <Box
              component="span"
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              fontWeight={500}
            >
              {isCreditNote ? tl("billing.refundAmount") : tl("billing.additionalPayment")}
            </Box>

            <Box component="span" marginLeft="16px" textAlign="left">
              <TextField
                disabled
                data-testmation="billingPaidAmount"
                value={
                  isCreditNote
                    ? Number(paymentInfo?.paidAmount ?? 0).toFixed(2)
                    : Number(paymentInfo?.paymentDistribution?.additionalPayment ?? 0).toFixed(2)
                }
                margin="dense"
                variant="outlined"
                onFocus={(e) => e.target.select()}
                slotProps={{
                  htmlInput: { allowNegative: isCreditNote },
                  input: {
                    inputComponent: NrsFormat,
                    startAdornment: tl("rs"),
                    classes: { input: styles.nrsInput }
                  }
                }}
              />
            </Box>
          </Box>

          {!isCreditNote && !isSsfBilling && (
            <Box component="div" marginBottom="4px" display="flex">
              <Box
                component="span"
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                width="100%"
                fontWeight={500}
              >
                {tl("billing.paidFromBalance")}
              </Box>
              <Box component="span" marginLeft="16px" textAlign="left">
                <TextField
                  disabled
                  data-testmation="billingPaidFromBalance"
                  value={(paymentInfo.paymentDistribution?.paymentFromBalance || 0).toFixed(2)}
                  margin="dense"
                  variant="outlined"
                  slotProps={{
                    input: {
                      inputComponent: NrsFormat as any,
                      startAdornment: tl("rs"),
                      classes: { input: styles.nrsInput }
                    }
                  }}
                />
              </Box>
            </Box>
          )}

          {!isCreditNote && (
            <Typography>
              <Box component="div" display="flex">
                <Box
                  component="span"
                  fontSize="0.83em"
                  display="inline-block"
                  width="100%"
                  fontWeight={500}
                >
                  {tl("billing.totalPaid")}
                </Box>

                <Box
                  component="span"
                  fontSize="0.83em"
                  marginLeft="15px"
                  fontWeight={500}
                  textAlign="right"
                >
                  <TextField
                    value={(draft.paymentInfo.paidAmount || 0).toFixed(2)}
                    className={styles.nrsReadInputRoot}
                    disabled
                    slotProps={{
                      htmlInput: { allowNegative: isCreditNote },
                      input: {
                        inputComponent: NrsFormat,
                        startAdornment: tl("rs"),
                        classes: {
                          root: styles.nrsReadInputRoot,
                          input: styles.nrsReadInput
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            </Typography>
          )}

          {/* TODO: might need change */}
          {/* {(draft?.summary?.totalAmount !== paymentInfo?.paidAmount ||
            (isCreditNote && draft.refundInfo.maximumRefundable > 0)) && ( */}
          {(draft?.summary?.totalAmount !== paymentInfo?.paidAmount ||
            (isCreditNote && (draft?.refundInfo?.maximumRefundable || 0) > 0)) && (
            <Typography>
              <Box component="span" display="flex">
                <Box
                  component="span"
                  fontSize="0.83em"
                  display="inline-block"
                  width="100%"
                  fontWeight={500}
                >
                  {isCreditNote ? tl("billing.remaingRefundable") : tl("billing.dueAmount")}
                </Box>
                <Box
                  component="span"
                  fontSize="0.83em"
                  marginLeft="15px"
                  fontWeight={500}
                  textAlign="right"
                >
                  <TextField
                    value={dueAmt}
                    margin="dense"
                    className={styles.nrsReadInputRoot}
                    disabled
                    slotProps={{
                      htmlInput: { allowNegative: isCreditNote },
                      input: {
                        inputComponent: NrsFormat,
                        startAdornment: t("rs"),
                        classes: {
                          root: styles.nrsReadInputRoot,
                          input: styles.nrsReadInput
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            </Typography>
          )}
        </>
      )}

      {/* {draft.paymentInfo.paymentMethod === paymentOptionsEnum.cash &&
        paymentInfo.paid &&
        !isCreditNote && ( */}
      {draft.paymentInfo.paymentBreakdown.length === 1 &&
        draft.paymentInfo.paymentBreakdown[0].method === paymentOptionsEnum.cash &&
        paymentInfo.paid &&
        !isCreditNote && (
          <Box sx={{ my: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography component="label">{tl("billing.tenderAmount")}</Typography>
              <TextField
                data-testmation="tenderAmountInput"
                slotProps={{
                  input: {
                    inputComponent: NrsFormat,
                    startAdornment: tl("rs"),
                    classes: { input: styles.nrsInput }
                  }
                }}
                onFocus={(e) => e.target.select()}
                margin="dense"
                variant="outlined"
                value={draft.paymentInfo.tenderAmount}
                onChange={(event) => {
                  const changeAmount =
                    Number(event.target.value) > draft.summary.totalAmount
                      ? Number(event.target.value) - draft.summary.totalAmount
                      : 0;
                  updateDraft({
                    ...draft,
                    paymentInfo: {
                      ...draft.paymentInfo,
                      tenderAmount: Number(event.target.value),
                      changeAmount
                    }
                  });
                }}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography component="label">{tl("billing.changeAmount")}</Typography>
              <TextField
                disabled
                value={round(draft.paymentInfo.changeAmount || 0, 2).toFixed(2)}
                slotProps={{
                  input: {
                    inputComponent: NrsFormat,
                    startAdornment: tl("rs"),
                    classes: { input: styles.nrsInput }
                  }
                }}
                margin="dense"
                variant="outlined"
              />
            </Box>
          </Box>
        )}
    </FormGroup>
  );
};

export default PaymentInfo;
